/**
 * 서명 토큰 수명주기 — Option S 웹 배치(AUDIO_SPLIT §7-1).
 *
 * 서비스 분리 후 stream 프로세스에는 세션이 **없다**(세션은 auth 메모리
 * 전용). 그래서 브라우저가 자격을 운반한다: auth 가 발급한 서명 토큰을
 * stream 의 REST·WS 에 Bearer/서브프로토콜로 붙인다 — 서버간 호출 0.
 *
 * 두 축의 토큰을 여기서 관리한다:
 *
 *  1. **계정 토큰**(account/admin) — 로그인 세션 쿠키로 `/api/token`(auth)
 *     에서 발급. 방 개설 권한(cap:create)·관리 권한(cap:admin)을 싣고
 *     stream 의 방 개설·방 관리 라우트에 Bearer 로 붙는다.
 *     **메모리에만 둔다**: 필요할 때 쿠키로 언제든 재발급되는 파생물이라
 *     저장할 이유가 없고, 저장하면 XSS 노출 표면만 는다.
 *
 *  2. **방 토큰**(host/viewer) — 개설/입장/링크 교환 응답이 준다. WS
 *     핸드셰이크와 방 REST 의 자격이다. 이쪽은 세션 스토리지(RoomCredential)
 *     에 함께 보관한다 — 이유:
 *       · 재교환 비밀(패스코드·직통 링크 비밀)이 **이미** 같은 자리에 있다.
 *         10분짜리 토큰이 추가하는 노출은 0 이다.
 *       · F5(새로고침) 가 회의를 끊지 않아야 한다. 메모리에만 두면
 *         새로고침마다 auth 왕복(호스트) 이 필요해져 "auth 다운 → 회의
 *         무영향" 이 새로고침 한 번에 깨진다.
 *       · localStorage 는 쓰지 않는다 — 탭을 닫으면 자격도 소멸해야 한다.
 *
 * 갱신 규율(TTL 10분):
 *  · 호스트: `/api/rooms/{room}/token` (stream, 계정 토큰/세션 쿠키) —
 *    만료 2분 전 선갱신.
 *  · 뷰어: 패스코드 재교환 `/api/rooms/join`, 직통 링크 뷰어는 같은 비밀로
 *    `/api/rooms/{room}/link` (둘 다 stream!) — auth 가 죽어도 갱신된다
 *    (§5.4 익명 경로는 계정 저장소 무접촉).
 *  · **stream 단독 갱신** `/api/rooms/{room}/refresh`(TASK_sec_fix_e #1/#3):
 *    아직 유효한 이 방의 토큰을 내밀면 같은 역할의 새 토큰을 준다 — auth
 *    비의존. 위 경로가 실패했을 때의 폴백이고(호스트: auth 다운·분리 401),
 *    관리자 참관 뷰어(패스코드도 링크 비밀도 없다)에게는 **유일한** 경로다.
 *    만료된 토큰으로는 안 된다 — 만료 뒤에는 위 경로만 남는다.
 *  · 열린 WS 는 서버가 중간 재검증하지 않는다(설계 결정) — 갱신은 소켓을
 *    건드리지 않고, 다음 재접속·다음 REST 호출이 새 토큰을 쓴다.
 *  · 갱신 실패 시 더 내줄 자격이 없다 — transport 가 terminated 를 올리고
 *    화면이 말한다(조용히 죽은 UI 금지). 구 host_key/view_key 폴백은
 *    **TRANS_LEGACY_ROOM_KEYS=1 서버가 준 경우에만**(cred.key 비어 있지
 *    않을 때) 남는다 — 기본 형상에서는 그 값이 없다.
 */

import { API_BASE } from './config'
import { saveCredential, type RoomCredential } from './session'

/** 만료 이만큼 전에 미리 갱신한다(라이브 회의 무중단 — 만료 경계 회피). */
export const REFRESH_MARGIN_MS = 120_000
/** 시계 오차 여유 — 이 안쪽 잔여 수명은 "이미 만료" 로 취급한다. */
export const SKEW_MS = 15_000
/** 갱신 실패 후 재시도 간격(백오프) — auth 다운 중 폭주 방지. */
const RETRY_INTERVAL_MS = 60_000
/** 선갱신 점검 주기. */
const CHECK_INTERVAL_MS = 30_000

interface Minted {
  token: string
  /** epoch ms */
  exp: number
}

/** 서버 `/api/token`·`/api/rooms/join` 응답의 토큰 조각. */
interface TokenPayload {
  token?: string
  token_ttl_s?: number
}

function mintedFrom(body: TokenPayload): Minted | null {
  if (!body.token) return null
  const ttlS = typeof body.token_ttl_s === 'number' ? body.token_ttl_s : 600
  return { token: body.token, exp: Date.now() + ttlS * 1000 }
}

function fresh(m: Minted | null): boolean {
  return m !== null && Date.now() < m.exp - SKEW_MS
}

/* ── 1) 계정 토큰(cap:create/admin) — 메모리 전용 ──────────────────────── */

let accountCache: Minted | null = null
let accountInflight: Promise<Minted | null> | null = null

/**
 * `/api/token` 발급 — 실패는 null(네트워크·401·구 서버 404 전부).
 * 실패를 던지지 않는 이유: 단일 프로세스(both)에서는 세션 쿠키만으로도
 * 같은 요청이 성공하므로, 헤더 없이 보내고 서버 판정에 맡기는 것이
 * 두 형상 모두에서 올바른 동작이다.
 */
async function mintAccountToken(): Promise<Minted | null> {
  try {
    const res = await fetch(`${API_BASE}/token`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    if (!res.ok) return null
    return mintedFrom((await res.json()) as TokenPayload)
  } catch {
    return null
  }
}

/**
 * stream 으로 가는 계정 단위 요청(방 개설·방 관리)에 붙일 인증 헤더.
 *
 * 반환이 `{}` 일 수 있다 — 발급 실패 시 요청은 세션 쿠키만 싣고 나가고,
 * 분리 형상에서는 서버가 401 로 답해 화면 문구("로그인이 필요합니다")로
 * 표면화된다. 여기서 미리 던지면 단일 프로세스 형상(쿠키로 충분)까지
 * 막아 버린다.
 */
export async function accountAuthHeaders(): Promise<Record<string, string>> {
  if (fresh(accountCache) && accountCache!.exp - Date.now() > REFRESH_MARGIN_MS) {
    return { Authorization: `Bearer ${accountCache!.token}` }
  }
  // 발급 왕복은 동시 호출을 합친다(관리 화면이 목록 3개를 병렬로 부른다).
  accountInflight ??= mintAccountToken().finally(() => {
    accountInflight = null
  })
  const minted = await accountInflight
  if (minted) accountCache = minted
  // 발급 실패여도 아직 유효한 캐시가 있으면 그걸 쓴다(일시 장애 흡수).
  if (fresh(accountCache)) {
    return { Authorization: `Bearer ${accountCache!.token}` }
  }
  accountCache = null
  return {}
}

/** 로그인·로그아웃 시 반드시 부른다 — 이전 계정의 토큰이 살아남으면 안 된다. */
export function clearAccountToken(): void {
  accountCache = null
}

/* ── 2) 방 토큰(host/viewer) — RoomCredential 과 함께 수명 관리 ────────── */

/**
 * 자격이 종결됐는가(DEF-018 §1.2). `credential_dead` 면 타이머는 이미
 * 스스로 멈췄다 — 죽은 방을 향한 60s 재시도 폭주가 입장 백오프를 채워
 * **다른 살아있는 방** 입장까지 막던 것이 DEF-018 이다.
 */
export type RoomAuthEnd = 'alive' | 'credential_dead'

export interface RoomAuth {
  /**
   * 지금 요청·핸드셰이크에 붙일 자격 — 신선한 토큰. 없으면 전환기 구 키
   * (서버가 준 경우), 그마저 없으면 빈 문자열 — 서버가 4401/403 으로
   * 거절하고 그 거절이 `refresh()` 를 부른다(만료 토큰으로 재접속하지 않는다).
   */
  current(): string
  /**
   * 강제 재교환(4401 등 자격 거부 시 transport 가 부른다).
   * 반환: 방금 거부된 것과 **다른** 자격, 더 시도할 것이 없으면 null
   * (→ transport 가 terminated 로 종결한다).
   */
  refresh(): Promise<string | null>
  /** 선갱신 타이머 시작(멱등). */
  start(): void
  stop(): void
  /** 신규 — 자격이 종결됐는가. 'credential_dead' 면 타이머는 이미 스스로 멈췄다. */
  status(): RoomAuthEnd
}

/**
 * 방 자격 관리자. `cred` 는 세션 스토리지의 RoomCredential — 토큰 갱신은
 * 스토리지에도 즉시 반영한다(F5 생존).
 */
export function createRoomAuth(cred: RoomCredential): RoomAuth {
  let token: Minted | null =
    cred.token && cred.tokenExp
      ? { token: cred.token, exp: cred.tokenExp }
      : null
  /** 마지막으로 current() 가 내준 자격 — refresh() 가 "같은 것 재발급" 을 피한다. */
  let lastIssued = ''
  let timer: ReturnType<typeof setInterval> | null = null
  let lastAttemptAt = 0
  let refreshing: Promise<string | null> | null = null
  /**
   * 자격 거부(403/404) **연속** 횟수. 2회가 종결 문턱이다(§1.2).
   *
   * 1회가 아닌 이유: 재기동 순간의 일시적 403 을 영구 종결로 오판하지
   * 않기 위해서다. 429·네트워크 오류는 **종결이 아니다** — 일시적이고,
   * 오프라인 복귀를 막으면 안 된다. 그래서 그 경우엔 이 카운터를 건드리지
   * 않는다(올리지도, 내리지도 않는다).
   */
  let denied = 0
  let dead = false
  /**
   * 직통 링크 재교환 비밀. `linkKey` 가 정본이지만, 옛 번들이 저장한 자격은
   * 그 자리에 구 view_key(`key`)만 있다 — 같은 값이 곧 `/link` 교환 입력
   * 이므로(TASK_sec_fix_e #2) 그것을 쓰고, 성공하면 linkKey 로 이행해
   * 저장한다.
   */
  const linkSecret =
    cred.linkKey ||
    (cred.role === 'viewer' && !cred.passcode ? cred.key : '')

  const persist = (): void => {
    saveCredential({
      ...cred,
      ...(token ? { token: token.token, tokenExp: token.exp } : {}),
      ...(linkSecret && !cred.linkKey ? { linkKey: linkSecret } : {}),
    })
  }

  /**
   * 응답 상태코드를 **소실 판정으로 승격**한다(DEF-018 §1.2).
   *
   * 403/404 **연속 2회**만 종결이다. 429 와 네트워크 오류(= 이 함수가
   * 불리지 않는 경로)는 종결 신호가 아니므로 카운터를 건드리지 않는다 —
   * 일시적 혼잡·오프라인을 "회의 종료" 로 확정하면 사용자가 살아 있는
   * 회의를 포기한다.
   */
  const noteStatus = (status: number): void => {
    if (status === 403 || status === 404) {
      denied += 1
      if (denied >= 2) {
        dead = true
        stopTimer()
      }
      return
    }
    // **연속이 아니면 연속이 아니다**(codex #6). 429 나 네트워크 오류가
    // 사이에 끼면 그 둘은 이어진 거부가 아니다 — 실측으로 `403→429→403`
    // 과 `403→네트워크오류→403` 이 credential_dead 를 만들었다. 일시적
    // 혼잡·오프라인이 낀 것을 영구 종결로 읽으면, 살아 있는 회의의 탭이
    // 스스로 갱신을 포기한다.
    denied = 0
  }

  /** 네트워크 오류 — 응답이 없으므로 `noteStatus` 가 불리지 않는다. */
  const noteNetworkError = (): void => {
    denied = 0
  }

  const stopTimer = (): void => {
    if (timer) clearInterval(timer)
    timer = null
  }

  /**
   * 호스트 — **stream 의** `/api/rooms/{room}/token` 재발급.
   *
   * auth 의 `/api/token {room}` 이 아닌 이유: 소유 판정이 로컬 레지스트리를
   * 보는데 분리 형상의 auth 프로세스에는 방이 없다(분리 e2e 실측 — 항상
   * 403). 신원은 계정 토큰(Bearer, 분리) 또는 세션 쿠키(단일 프로세스)가
   * 운반하고, 방을 소유한 stream 이 소유를 판정한다.
   */
  const mintHost = async (): Promise<Minted | null> => {
    try {
      const res = await fetch(
        `${API_BASE}/rooms/${encodeURIComponent(cred.room)}/token`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json',
                     ...(await accountAuthHeaders()) },
        },
      )
      if (!res.ok) {
        noteStatus(res.status)
        return null
      }
      noteStatus(200)
      return mintedFrom((await res.json()) as TokenPayload)
    } catch {
      noteNetworkError()
      return null
    }
  }

  /**
   * 직통 링크(#k=) 뷰어 — 같은 비밀로 `/link` 를 다시 지난다(패스코드
   * 재교환과 같은 규율, 같은 stream 소유 경로). 거절(403)은 방이 닫혔거나
   * 비공개로 바뀐 것이다 — noteStatus 가 연속 2회를 종결로 센다.
   */
  const relink = async (): Promise<Minted | null> => {
    if (!linkSecret) return null
    try {
      const res = await fetch(
        `${API_BASE}/rooms/${encodeURIComponent(cred.room)}/link`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: linkSecret }),
        },
      )
      if (!res.ok) {
        noteStatus(res.status)
        return null
      }
      noteStatus(200)
      const body = (await res.json()) as TokenPayload & { room?: string }
      if (body.room !== cred.room) return null
      return mintedFrom(body)
    } catch {
      noteNetworkError()
      return null
    }
  }

  /**
   * 뷰어 — 패스코드 재교환(§5.2 "재교환이 곧 갱신이다"). stream 소유
   * 경로라 **auth 가 죽어 있어도** 갱신된다. 직통 링크(#k=) 뷰어는
   * 패스코드가 없다 — 그쪽은 `relink` 가 같은 일을 한다.
   */
  const rejoin = async (): Promise<Minted | null> => {
    if (!cred.passcode) return relink()
    try {
      const res = await fetch(`${API_BASE}/rooms/join`, {
        method: 'POST',
        credentials: 'same-origin',
        // 계정 토큰이 있으면 함께 싣는다(§5.5 신원 축) — 로그인 뷰어의
        // 채팅 이름 점유 주체(acct:)가 토큰 갱신을 건너 이어지게 한다.
        // 익명이면 발급이 조용히 실패해 빈 헤더다(입장은 그대로 진행).
        headers: {
          'Content-Type': 'application/json',
          ...(await accountAuthHeaders()),
        },
        body: JSON.stringify({ passcode: cred.passcode }),
      })
      if (!res.ok) {
        noteStatus(res.status)
        return null
      }
      noteStatus(200)
      const body = (await res.json()) as TokenPayload & { room?: string }
      // 같은 패스코드가 **다른 방**을 가리키게 됐다면(방 재개설) 이 토큰은
      // 이 화면의 방 자격이 아니다 — 갱신 실패로 처리한다.
      if (body.room !== cred.room) return null
      return mintedFrom(body)
    } catch {
      noteNetworkError()
      return null
    }
  }

  /**
   * stream 단독 갱신 — 아직 유효한 토큰으로 `/refresh`(TASK_sec_fix_e #1/#3).
   *
   * auth 가 죽어도(호스트의 `/token` 이 401·네트워크 오류) 방을 소유한
   * stream 만으로 만료 전에 갈아탄다 — "인증 서버가 죽어도 진행 중인 회의는
   * 계속된다". 관리자 참관 뷰어는 재교환 비밀이 없어 이 경로뿐이다.
   * 만료된 토큰은 서버가 받지 않으므로 여기서도 보내지 않는다(fresh 만).
   *
   * `countDenials` — 이 경로가 **유일한** 경로일 때만 403 을 종결 카운터에
   * 센다. 폴백으로 불린 경우 앞선 경로가 이미 이번 시도의 거부를 셌다 —
   * 한 번의 갱신 시도가 "연속 2회" 를 혼자 채우면 안 된다(§1.2).
   */
  const refreshViaStream = async (countDenials: boolean): Promise<Minted | null> => {
    if (!fresh(token)) return null
    try {
      const res = await fetch(
        `${API_BASE}/rooms/${encodeURIComponent(cred.room)}/refresh`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Authorization: `Bearer ${token!.token}` },
        },
      )
      if (!res.ok) {
        if (countDenials) noteStatus(res.status)
        return null
      }
      noteStatus(200)
      const body = (await res.json()) as TokenPayload & { room?: string }
      if (body.room !== cred.room) return null
      return mintedFrom(body)
    } catch {
      noteNetworkError()
      return null
    }
  }

  const doRefresh = async (): Promise<string | null> => {
    lastAttemptAt = Date.now()
    let minted: Minted | null = null
    let primary = true
    if (cred.role === 'host') minted = await mintHost()
    else if (cred.passcode || linkSecret) minted = await rejoin()
    else primary = false // 관리자 참관 — 재교환 비밀이 없다
    if (!minted) minted = await refreshViaStream(!primary)
    if (minted) {
      token = minted
      persist()
      lastIssued = minted.token
      return minted.token
    }
    // 전환기 폴백 — 서버가 구 키를 준 형상(TRANS_LEGACY_ROOM_KEYS=1)에서만
    // cred.key 가 있다. 방금 거부된 자격이 구 키가 아니라면 구 키를 내준다.
    // 기본 형상(cred.key 빈 문자열)에서는 여기로 오지 않는다 — 더 없다,
    // null(terminated).
    if (cred.key && lastIssued !== cred.key) {
      lastIssued = cred.key
      return cred.key
    }
    return null
  }

  return {
    current(): string {
      if (fresh(token)) {
        lastIssued = token!.token
        return token!.token
      }
      lastIssued = cred.key
      return cred.key
    },
    refresh(): Promise<string | null> {
      // 동시 4401(REST 실패 + WS 재접속)을 한 번의 재교환으로 합친다.
      refreshing ??= doRefresh().finally(() => {
        refreshing = null
      })
      return refreshing
    },
    status(): RoomAuthEnd {
      return dead ? 'credential_dead' : 'alive'
    },
    start(): void {
      // 종결된 자격은 다시 돌지 않는다 — 자기정지의 의미가 없어진다.
      if (timer || dead) return
      timer = setInterval(() => {
        if (!token) return // 구 서버(토큰 없음) — 갱신할 것이 없다
        const remaining = token.exp - Date.now()
        if (remaining > REFRESH_MARGIN_MS) return
        if (Date.now() - lastAttemptAt < RETRY_INTERVAL_MS) return // 백오프
        void doRefresh()
      }, CHECK_INTERVAL_MS)
    },
    stop(): void {
      stopTimer()
    },
  }
}
